package io.horion.cv.components.forms

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue

import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.toAttrs

import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode

import io.horion.cv.components.dynamicfields.DynamicField
import io.horion.cv.components.dynamicfields.DynamicFieldsSection
import io.horion.cv.components.widgets.FileInput
import io.horion.cv.components.widgets.TextFormField
import io.horion.cv.di.koinGet
import io.horion.cv.styles.ActivityFullWidthFieldStyle
import io.horion.cv.styles.ActivityHalfWidthFieldStyle
import io.horion.cv.styles.DateSelectorStyle
import io.horion.cv.styles.PersonalDetailsFormStyle
import io.horion.cv.styles.PersonalDetailsGroupColumnStyle
import io.horion.cv.styles.PersonalDetailsHalfWidthStyle
import io.horion.cv.styles.PersonalDetailsMainRowStyle
import io.horion.cv.styles.PersonalDetailsPhotoStyle
import io.horion.cv.styles.PersonalDetailsRowStyle
import io.horion.cv.utils.Colors
import io.horion.cv.utils.Constants.INTER
import io.horion.cv.viewModel.CvViewModel

import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.url.URL
import org.w3c.files.File


@Composable
fun PersonalDetailsForm() {

    val cvViewModel = koinGet<CvViewModel>()
    val cvUiState = cvViewModel.cvUiState
    val pi = cvUiState.data.personalInformation

    val allFields = listOf(
        DynamicField("permis", "Permis de Conduire", 240.px),
        DynamicField("etat", "État civil", 140.px),
        DynamicField("nat", "Nationalité", 160.px),
        DynamicField("lieu", "Lieu de naissance", 240.px),
        DynamicField("adresse", "Adresse", 140.px),
        DynamicField("linkedin", "LinkedIn", 140.px),
        DynamicField("site", "Site internet", 170.px),
        DynamicField("custom", "Champ personnalisé", 250.px)
    )

    Column(
        modifier = PersonalDetailsFormStyle.toModifier()
    ) {

        Row(
            modifier = PersonalDetailsMainRowStyle.toModifier()
        ) {

            PhotoInput(
                imageUrl = pi.photo,
                onPhotoSelected = { newUrl ->
                    cvViewModel.updatePersonalInfo {
                        it.copy(photo = newUrl)
                    }
                },
                onFileSelected = { file ->
                    cvViewModel.updatePhoto(file)
                },
                modifier = PersonalDetailsPhotoStyle.toModifier()
            )

            Column(
                modifier = PersonalDetailsGroupColumnStyle.toModifier()
            ) {
                Row(
                    modifier = PersonalDetailsRowStyle.toModifier()
                ) {

                    TextFormField(
                        label = "Prénom(s)",
                        value = pi.firstName,
                        onValueChange = { value ->
                            cvViewModel.updatePersonalInfo {
                                it.copy(firstName = value)
                            }
                        },
                        modifier = PersonalDetailsHalfWidthStyle.toModifier(),
                        inputId = "input-firstName"
                    )

                    TextFormField(
                        label = "Nom(s) de famille",
                        value = pi.lastName,
                        onValueChange = { value ->
                            cvViewModel.updatePersonalInfo {
                                it.copy(lastName = value)
                            }
                        },
                        modifier = PersonalDetailsHalfWidthStyle.toModifier(),
                        inputId = "input-lastName"
                    )
                }

                TextFormField(
                    label = "Emploi recherché",
                    value = pi.bp,
                    onValueChange = { value ->
                        cvViewModel.updatePersonalInfo {
                            it.copy(bp = value)
                        }
                    },
                    modifier = ActivityFullWidthFieldStyle.toModifier(),
                    inputId = "input-bp"
                )
            }
        }

        Row(
            modifier = PersonalDetailsRowStyle.toModifier()
        ) {

            TextFormField(
                label = "Adresse e-mail",
                value = pi.email,
                onValueChange = { value ->
                    cvViewModel.updatePersonalInfo {
                        it.copy(email = value)
                    }
                },
                modifier = PersonalDetailsHalfWidthStyle.toModifier(),
                inputId = "input-email"
            )

            TextFormField(
                label = "Ville",
                value = pi.city,
                onValueChange = { value ->
                    cvViewModel.updatePersonalInfo {
                        it.copy(city = value)
                    }
                },
                modifier = PersonalDetailsHalfWidthStyle.toModifier(),
                inputId = "input-city"
            )
        }

        FormFieldIcon(
            label = "Date de naissance",
            value = pi.birth,
            onValueChanged = { value ->
                cvViewModel.updatePersonalInfo {
                    it.copy(birth = value)
                }
            },
            modifier = Modifier.fillMaxWidth()
        )

        Row(
            modifier = PersonalDetailsRowStyle.toModifier()
        ) {

            TextFormField(
                label = "Numéro de téléphone",
                value = pi.phone,
                onValueChange = { value ->
                    cvViewModel.updatePersonalInfo {
                        it.copy(phone = value)
                    }
                },
                modifier = PersonalDetailsHalfWidthStyle.toModifier(),
                inputId = "input-phone"
            )

            TextFormField(
                label = "Sexe",
                value = pi.sex,
                onValueChange = { value ->
                    cvViewModel.updatePersonalInfo {
                        it.copy(sex = value)
                    }
                },
                modifier = PersonalDetailsHalfWidthStyle.toModifier()
            )
        }

        DynamicFieldsSection(
            fields = allFields,
            cvViewModel = cvViewModel
        )
    }
}


@Composable
fun FormFieldIcon(
    label: String,
    value: String,
    onValueChanged: (String) -> Unit,
    modifier: Modifier = Modifier,
) {

    var dateInput by remember {
        mutableStateOf<HTMLInputElement?>(null)
    }

    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(8.px)
    ) {

        SpanText(
            text = label,
            modifier = Modifier
                .color(
                    if (isDark) Colors.White
                    else Colors.Primary
                )
                .fontSize(14.px)
                .fontFamily(INTER)
                .fontWeight(FontWeight.Light)
        )

        Box(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.CenterEnd
        ) {

            Input(
                type = InputType.Date,
                attrs = DateSelectorStyle
                    .toModifier()
                    .toAttrs {

                        value(value)

                        ref { element ->
                            dateInput = element

                            onDispose {
                                dateInput = null
                            }
                        }

                        onInput { event ->
                            onValueChanged(event.value)
                        }
                    }
            )

            Image(
                src = "calendar.svg",
                description = "calendar icon",
                modifier = Modifier
                    .size(20.px)
                    .margin(right = 20.px)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        dateInput
                            ?.asDynamic()
                            ?.showPicker()
                    }
            )
        }
    }
}


@Composable
private fun PhotoInput(
    imageUrl: String?,
    onPhotoSelected: (String) -> Unit,
    modifier: Modifier = Modifier,
    onFileSelected: (File) -> Unit,
) {

    var fileInput: HTMLInputElement? by remember {
        mutableStateOf(null)
    }

    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    Column(
        verticalArrangement = Arrangement.spacedBy(8.px)
    ) {

        SpanText(
            text = "Photo",
            modifier = Modifier
                .color(
                    if (isDark) Colors.White
                    else Colors.Primary
                )
                .fontSize(14.px)
                .fontFamily(INTER)
                .fontWeight(FontWeight.Light)
        )

        Box(
            modifier = modifier
                .borderRadius(5.px)
                .backgroundColor(
                    if (isDark) Colors.input
                    else Colors.LightGray
                )
                .cursor(Cursor.Pointer)
                .onClick {
                    fileInput?.click()
                },
            contentAlignment = Alignment.Center
        ) {

            if (!imageUrl.isNullOrBlank()) {

                Image(
                    src = imageUrl,
                    description = "photo view",
                    modifier = Modifier
                        .fillMaxSize()
                        .objectFit(ObjectFit.Cover)
                )

            } else {

                Image(
                    src = "/camera-add-photo.svg",
                    description = "Upload photo",
                    modifier = Modifier.size(28.px)
                )
            }
        }
    }

    FileInput(
        refCallback = {
            fileInput = it
        },
        onFileSelected = { file ->

            onFileSelected(file)

            val url = URL.createObjectURL(file)

            onPhotoSelected(url)
        }
    )
}